import { PERF_HUB_IMPROVING_FILTER_HELP } from './perfHubIntelligenceHelp';

/** CRM tag presets for Performance Hub creator shortlist. */
export const PERF_HUB_SHORTLIST_BASE = 'shortlist';
export const PERF_HUB_INTENT_TAGS = ['rebook', 'phase-2', 'ugc', 'improving'];

/** One-line labels for filters, tooltips, and the shortlist modal. */
export const PERF_HUB_SHORTLIST_INTENT_HELP = {
  rebook: 'Book this creator again on a future campaign.',
  'phase-2': 'Invite them for a second wave of this same campaign.',
  ugc: 'Tag this post for reuse in ads or owned channels—your internal record only.',
  improving: PERF_HUB_IMPROVING_FILTER_HELP,
};

export const PERF_HUB_SHORTLIST_INTENTS = [
  { id: 'rebook', label: 'Re-book', description: PERF_HUB_SHORTLIST_INTENT_HELP.rebook },
  { id: 'phase-2', label: 'Phase 2', description: PERF_HUB_SHORTLIST_INTENT_HELP['phase-2'] },
  { id: 'ugc', label: 'UGC', description: PERF_HUB_SHORTLIST_INTENT_HELP.ugc },
  { id: 'improving', label: 'Improving', description: PERF_HUB_SHORTLIST_INTENT_HELP.improving },
];

const REBOOK_SCORE_DEFAULT = 60;

/** Union of suggested intents across multiple creators (bulk shortlist defaults). */
export function suggestedShortlistIntentsForMany(creators) {
  const selected = new Set();
  for (const c of creators || []) {
    for (const id of suggestedShortlistIntents(c)) {
      selected.add(id);
    }
  }
  if (selected.size === 0) {
    selected.add('rebook');
  }
  return selected;
}

/** Suggested intents from intelligence signals (before CRM merge). */
export function suggestedShortlistIntents(creator) {
  const selected = new Set();
  if (!creator) {
    selected.add('rebook');
    return selected;
  }
  if ((creator.rebook_score ?? 0) >= REBOOK_SCORE_DEFAULT) {
    selected.add('rebook');
  }
  if (creator.ugc_suggested) {
    selected.add('ugc');
  }
  if (creator.phase2_recommended) {
    selected.add('phase-2');
  }
  if (creator.trend === 'improving') {
    selected.add('improving');
  }
  if (selected.size === 0) {
    selected.add('rebook');
  }
  return selected;
}

/** Initial checkbox state: existing CRM tags, else signal-based defaults. */
export function initialShortlistIntents(creator, crmEntry) {
  if (crmEntry && crmEntryShortlisted(crmEntry)) {
    const fromCrm = new Set();
    for (const id of PERF_HUB_INTENT_TAGS) {
      if ((crmEntry.tags || []).includes(id)) {
        fromCrm.add(id);
      }
    }
    if (fromCrm.size > 0) {
      return fromCrm;
    }
  }
  return suggestedShortlistIntents(creator);
}

/** Bulk modal: union of signals; single creator with CRM uses initialShortlistIntents. */
export function initialShortlistIntentsForModal(creators, crmList) {
  const list = (creators || []).filter(Boolean);
  if (list.length === 1) {
    const crmEntry = getCrmEntryForHandle(crmList, list[0].instagram_handle);
    return initialShortlistIntents(list[0], crmEntry);
  }
  if (list.length > 1) {
    return suggestedShortlistIntentsForMany(list);
  }
  return new Set(['rebook']);
}

export function isSuggestedShortlistIntent(intentId, creator) {
  if (!creator) return intentId === 'rebook';
  return isSuggestedShortlistIntentForOne(intentId, creator);
}

export function isSuggestedShortlistIntentForMany(intentId, creators) {
  return (creators || []).some((c) => isSuggestedShortlistIntentForOne(intentId, c));
}

function isSuggestedShortlistIntentForOne(intentId, creator) {
  if (!creator) return intentId === 'rebook';
  switch (intentId) {
    case 'rebook':
      return (creator.rebook_score ?? 0) >= REBOOK_SCORE_DEFAULT;
    case 'ugc':
      return Boolean(creator.ugc_suggested);
    case 'phase-2':
      return Boolean(creator.phase2_recommended);
    case 'improving':
      return creator.trend === 'improving';
    default:
      return false;
  }
}

export function crmEntryCustomTags(crmEntry) {
  return (crmEntry?.tags || []).filter(
    (t) => t && t !== PERF_HUB_SHORTLIST_BASE && !PERF_HUB_INTENT_TAGS.includes(t),
  );
}

/** Default empty manual CRM form (Performance Hub CRM tab). */
export const EMPTY_CRM_FORM = {
  instagram_handle: '',
  email: '',
  notes: '',
  tags: '',
  intents: [],
  source_campaign_name: '',
};

/** Prefill manual CRM form when a handle already exists. */
export function crmFormStateFromEntry(crmEntry) {
  if (!crmEntry) return { ...EMPTY_CRM_FORM };
  return {
    instagram_handle: crmEntry.instagram_handle || '',
    email: crmEntry.email || '',
    notes: crmEntry.notes || '',
    tags: crmEntryCustomTags(crmEntry).join(', '),
    intents: crmEntryIntentTags(crmEntry),
    source_campaign_name: primaryCrmCampaignName(crmEntry) || '',
  };
}

export function parseCustomCrmTagInput(tagsString) {
  return (tagsString || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((t) => t !== PERF_HUB_SHORTLIST_BASE && !PERF_HUB_INTENT_TAGS.includes(t));
}

/**
 * Merge shortlist intents + custom tags without wiping existing intents on update.
 */
export function buildCrmTagsForSave({ existingEntry, selectedIntents, customTagsString }) {
  const custom = parseCustomCrmTagInput(customTagsString);
  const intents = (Array.isArray(selectedIntents) ? selectedIntents : []).filter((id) =>
    PERF_HUB_INTENT_TAGS.includes(id),
  );

  if (intents.length > 0) {
    const merged = mergeShortlistTags(existingEntry?.tags, intents);
    return [...new Set([...merged, ...custom])];
  }

  const existing = existingEntry?.tags || [];
  const preserved = existing.filter(
    (t) => t === PERF_HUB_SHORTLIST_BASE || PERF_HUB_INTENT_TAGS.includes(t),
  );
  if (preserved.length) {
    return [...new Set([...preserved, ...custom])];
  }

  return custom;
}

export function mergeShortlistTags(existingTags, intentIds) {
  const ids = (Array.isArray(intentIds) ? intentIds : [intentIds]).filter((id) =>
    PERF_HUB_INTENT_TAGS.includes(id)
  );
  const unique = [...new Set(ids.length ? ids : ['rebook'])];
  const base = Array.isArray(existingTags) ? existingTags.filter(Boolean) : [];
  const custom = base.filter(
    (t) => t !== PERF_HUB_SHORTLIST_BASE && !PERF_HUB_INTENT_TAGS.includes(t)
  );
  return [PERF_HUB_SHORTLIST_BASE, ...unique, ...custom];
}

export function formatShortlistIntentLabels(intentIds) {
  const map = Object.fromEntries(PERF_HUB_SHORTLIST_INTENTS.map((o) => [o.id, o.label]));
  return (intentIds || []).map((id) => map[id] || id).join(', ');
}

export function crmEntryHasIntent(crmEntry, intentId) {
  const tags = crmEntry?.tags || [];
  return tags.includes(intentId);
}

export function crmEntryShortlisted(crmEntry) {
  const tags = crmEntry?.tags || [];
  return tags.includes(PERF_HUB_SHORTLIST_BASE) || PERF_HUB_INTENT_TAGS.some((t) => tags.includes(t));
}

export function getCrmEntryForHandle(crmList, handle) {
  const h = (handle || '').trim().replace(/^@/, '').toLowerCase();
  if (!h) return null;
  return (
    (crmList || []).find(
      (c) => (c.instagram_handle || '').trim().replace(/^@/, '').toLowerCase() === h
    ) || null
  );
}

/** Campaign names tied to this CRM row (structured fields + legacy notes). */
export function extractCrmCampaignNames(crmEntry) {
  const names = new Set();
  const last = (crmEntry?.source_campaign_name || '').trim();
  if (last) names.add(last);
  for (const n of crmEntry?.campaign_names || []) {
    const s = String(n || '').trim();
    if (s) names.add(s);
  }
  const notes = crmEntry?.notes || '';
  const re = /^Performance Hub shortlist — (.+)$/gm;
  let m;
  while ((m = re.exec(notes)) !== null) {
    const line = (m[1] || '').split('\n')[0].trim();
    if (line) names.add(line);
  }
  return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

export function primaryCrmCampaignName(crmEntry) {
  const names = extractCrmCampaignNames(crmEntry);
  if (!names.length) return null;
  return names[names.length - 1];
}

export function partitionCrmEntries(crmList) {
  const shortlisted = [];
  const other = [];
  for (const entry of crmList || []) {
    if (crmEntryShortlisted(entry)) shortlisted.push(entry);
    else other.push(entry);
  }
  return { shortlisted, other };
}

export function collectCrmCampaignFilterOptions(crmList) {
  const names = new Set();
  for (const entry of crmList || []) {
    if (!crmEntryShortlisted(entry)) continue;
    for (const n of extractCrmCampaignNames(entry)) {
      names.add(n);
    }
  }
  return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

export function filterShortlistedCrm(crmList, { campaign = 'all', intent = 'all' } = {}) {
  let list = (crmList || []).filter(crmEntryShortlisted);
  if (campaign && campaign !== 'all') {
    list = list.filter((entry) => extractCrmCampaignNames(entry).includes(campaign));
  }
  if (intent && intent !== 'all' && PERF_HUB_INTENT_TAGS.includes(intent)) {
    list = list.filter((entry) => crmEntryHasIntent(entry, intent));
  }
  return list.sort((a, b) => {
    const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
    const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
    return tb - ta;
  });
}

export function formatCrmTagLabel(tag) {
  if (tag === PERF_HUB_SHORTLIST_BASE) return null;
  const map = Object.fromEntries(PERF_HUB_SHORTLIST_INTENTS.map((o) => [o.id, o.label]));
  return map[tag] || tag;
}

export function crmEntryIntentTags(crmEntry) {
  return (crmEntry?.tags || []).filter((t) => PERF_HUB_INTENT_TAGS.includes(t));
}
